/*!
 * czholo.js · CardZone · brillo holográfico de las cartas en la tienda
 *
 * Mismo efecto que la «Vitrina holo»: la carta se inclina en 3D siguiendo el
 * ratón o el dedo, se mueve sola cuando nadie la toca y la luz, el arcoíris,
 * la purpurina, la textura del dibujo y los destellos se mueven como en la
 * carta de verdad. Funciona en:
 *   - la foto de la ficha de producto (galería del tema, <product-gallery>)
 *   - el visor al ampliar (PhotoSwipe del tema)
 * En Android la luz sigue además la inclinación del teléfono.
 *
 * Solo actúa sobre fotos subidas por la app CardZone Singles con el efecto en
 * el nombre del archivo: -holo-holo | -holo-reverse | -holo-ultra | -holo-sir
 * | -holo-gold. Cualquier otra imagen (el resto de productos) no se toca.
 *
 * Prueba en cualquier ficha: añade ?czholo=sir (o ultra, gold, holo…) a la URL.
 *
 * Instalación en el tema (cardzone2.0): assets/czholo.js y, en
 * layout/theme.liquid, justo antes de </body>:
 *   <script src="{{ 'czholo.js' | asset_url }}" defer></script>
 *
 * Este archivo vive en la app (shopify-theme/czholo.js) solo para tenerlo
 * versionado junto a lib/foil.ts: la app NO lo sirve ni lo ejecuta.
 */
(() => {
  'use strict';
  if (window.__czholo) return;
  window.__czholo = true;

  const EFFECTS = ['none', 'holo', 'reverse', 'ultra', 'sir', 'gold'];
  const FILE_RE = /-holo-(none|holo|reverse|ultra|sir|gold)(?![a-z])/i;
  const forced = (new URLSearchParams(location.search).get('czholo') || '').toLowerCase();
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const REST = 0.18; // intensidad del brillo en reposo
  const TILT = 14; // grados máximos de inclinación
  const EDGE_EFFECTS = new Set(['sir', 'gold']);

  // (nunca la copia animada que mete este script: .czh-base)
  const GALLERY_IMG = 'product-gallery .product-gallery__media img:not(.czh-base)';
  const LIGHTBOX_IMG = '.pswp__img:not(.czh-base)';

  /* ---------- texturas (se generan una vez) ---------- */

  function rng(seed) {
    let s = seed % 2147483647 || 1;
    return () => (s = (s * 16807) % 2147483647) / 2147483647;
  }

  // purpurina en un mosaico que se repite sin costuras
  function glitterTexture(size, count, seed) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const r = rng(seed);
    for (let i = 0; i < count; i++) {
      const x = r() * size, y = r() * size;
      const rad = 0.7 + Math.pow(r(), 2.4) * 1.8;
      const a = 0.25 + r() * 0.75;
      g.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`;
      for (const ox of [-size, 0, size]) for (const oy of [-size, 0, size]) {
        const px = x + ox, py = y + oy;
        if (px < -4 || py < -4 || px > size + 4 || py > size + 4) continue;
        g.beginPath(); g.arc(px, py, rad, 0, Math.PI * 2); g.fill();
      }
    }
    return c.toDataURL('image/png');
  }

  // destellos en forma de estrella de cuatro puntas
  function glintTexture(size, count, seed) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const r = rng(seed);
    for (let i = 0; i < count; i++) {
      const x = 20 + r() * (size - 40), y = 20 + r() * (size - 40);
      const len = 7 + r() * 11, w = 1.2 + r() * 1.1;
      for (const rot of [0, Math.PI / 2]) {
        g.save(); g.translate(x, y); g.rotate(rot + 0.35);
        const lg = g.createLinearGradient(-len, 0, len, 0);
        lg.addColorStop(0, 'rgba(255,255,255,0)');
        lg.addColorStop(0.5, 'rgba(255,255,255,1)');
        lg.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = lg; g.fillRect(-len, -w / 2, len * 2, w);
        g.restore();
      }
      const rg = g.createRadialGradient(x, y, 0, x, y, w * 2.2);
      rg.addColorStop(0, 'rgba(255,255,255,1)');
      rg.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = rg; g.beginPath(); g.arc(x, y, w * 2.2, 0, Math.PI * 2); g.fill();
    }
    return c.toDataURL('image/png');
  }

  /* ---------- textura que sigue los trazos del dibujo (sir / gold) ---------- */

  const EDGE_CACHE = new Map();

  function edgeMap(src) {
    if (!src) return Promise.resolve(null);
    if (EDGE_CACHE.has(src)) return EDGE_CACHE.get(src);
    const job = new Promise((resolve) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => {
        try { resolve(computeEdges(im)); } catch { resolve(null); }
      };
      im.onerror = () => resolve(null);
      im.src = src;
    });
    EDGE_CACHE.set(src, job);
    return job;
  }

  function computeEdges(im) {
    const W = 420;
    const H = Math.round(W * im.naturalHeight / im.naturalWidth);
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(im, 0, 0, W, H);
    const src = g.getImageData(0, 0, W, H).data; // lanza si la imagen no permite CORS
    const L = new Float32Array(W * H);
    for (let i = 0, j = 0; i < L.length; i++, j += 4) {
      // lo transparente (fuera de la carta) cuenta como negro: sin bordes ahí
      L[i] = src[j + 3] < 128 ? 0 : (src[j] * 0.299 + src[j + 1] * 0.587 + src[j + 2] * 0.114) / 255;
    }
    const M = new Float32Array(W * H);
    const BINS = 512, TOP = 3, hist = new Uint32Array(BINS);
    let n = 0;
    for (let y = 1; y < H - 1; y++) {
      for (let x = 1; x < W - 1; x++) {
        const i = y * W + x;
        const tl = L[i - W - 1], t = L[i - W], tr = L[i - W + 1];
        const l = L[i - 1], r = L[i + 1];
        const bl = L[i + W - 1], b = L[i + W], br = L[i + W + 1];
        const gx = (tr + 2 * r + br) - (tl + 2 * l + bl);
        const gy = (bl + 2 * b + br) - (tl + 2 * t + tr);
        const m = Math.sqrt(gx * gx + gy * gy);
        M[i] = m;
        if (L[i] > 0) { hist[Math.min(BINS - 1, (m / TOP * BINS) | 0)]++; n++; }
      }
    }
    const target = n * 0.9;
    let acc = 0, p90 = TOP;
    for (let k = 0; k < BINS; k++) { acc += hist[k]; if (acc >= target) { p90 = (k + 1) / BINS * TOP; break; } }
    // el grabado real no va sobre el texto impreso: se apaga cerca de lo casi negro
    const R = 2, tmp = new Float32Array(W * H), dark = new Float32Array(W * H);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      let m = 1;
      for (let k = -R; k <= R; k++) m = Math.min(m, L[y * W + clamp(x + k, 0, W - 1)]);
      tmp[y * W + x] = m;
    }
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      let m = 1;
      for (let k = -R; k <= R; k++) m = Math.min(m, tmp[clamp(y + k, 0, H - 1) * W + x]);
      dark[y * W + x] = m;
    }
    const lo = p90 * 0.3, span = Math.max(1e-3, p90 * 1.2);
    const out = g.createImageData(W, H), o = out.data;
    for (let i = 0, j = 0; i < M.length; i++, j += 4) {
      const keep = clamp((dark[i] - 0.14) / 0.22, 0, 1);
      const v = Math.pow(clamp((M[i] - lo) / span, 0, 1), 0.85) * keep * keep * (3 - 2 * keep);
      o[j] = o[j + 1] = o[j + 2] = 255;
      o[j + 3] = (v * 255) | 0;
    }
    g.putImageData(out, 0, 0);
    return c.toDataURL('image/png');
  }

  /* ---------- estilos (los de la vitrina) ---------- */

  // Ventana de la ilustración dentro de la imagen cuadrada de la casa
  // (1920×1920, carta centrada ocupando ~88 % del alto), como caja y como máscara
  const ART = 'left:23.6%;top:15.6%;width:52.8%;height:35.3%';
  const ART_MASK = '50% 24.1% / 52.8% 35.3%';

  function injectCSS() {
    const glitter = glitterTexture(512, 1900, 7);
    const glint = glintTexture(512, 9, 23);
    const rainbow = `repeating-linear-gradient(115deg,
      hsl(0 95% 64%) 0%, hsl(38 100% 60%) 4%, hsl(58 100% 58%) 8%, hsl(135 85% 56%) 12%,
      hsl(188 95% 58%) 16%, hsl(226 95% 66%) 20%, hsl(278 90% 66%) 24%, hsl(330 95% 64%) 28%, hsl(0 95% 64%) 32%)`;
    const rainbowV = `repeating-linear-gradient(90deg,
      hsl(0 95% 60%) 0%, hsl(55 100% 58%) 5%, hsl(140 85% 52%) 10%, hsl(195 95% 56%) 15%,
      hsl(250 90% 64%) 20%, hsl(310 90% 62%) 25%, hsl(0 95% 60%) 30%)`;
    const gold = `repeating-linear-gradient(120deg,
      hsl(40 70% 30%) 0%, hsl(47 100% 70%) 6%, hsl(36 85% 42%) 12%, hsl(52 100% 86%) 18%, hsl(40 70% 30%) 24%)`;
    const css = `
.czh-host{position:relative}
.czh{position:absolute;left:0;top:0;pointer-events:none;z-index:1;transform-origin:50% 50%;
  transform:perspective(1000px) rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg)) scale(var(--s,1));
  will-change:transform;
  filter:drop-shadow(calc(var(--sx,0) * 1px) calc(var(--sy,10) * 1px) 16px rgba(0,0,0,.5))}
.czh-face{position:absolute;inset:0;isolation:isolate;
  -webkit-mask-image:var(--czh-src);mask-image:var(--czh-src);
  -webkit-mask-size:100% 100%;mask-size:100% 100%;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat}
.czh-base{position:absolute;inset:0;width:100%!important;height:100%!important;max-width:none!important;
  display:block;margin:0;padding:0;border:0;border-radius:0!important;background:none!important;
  object-fit:fill!important;object-position:50% 50%!important;opacity:1!important;user-select:none}
.czh-l{position:absolute;inset:0;display:none;
  --P:calc(var(--px,.5) * 100%) calc(var(--py,.5) * 100%);
  --Pi:calc(100% - var(--px,.5) * 100%) calc(100% - var(--py,.5) * 100%)}

/* Reflejo de luz */
.czh-glare{display:block;mix-blend-mode:overlay;opacity:calc(var(--a,0) * .6);
  background:radial-gradient(farthest-corner circle at var(--P),
    rgba(255,255,255,.85) 8%,rgba(255,255,255,.4) 24%,rgba(0,0,0,.45) 100%)}
.czh[data-fx="none"] .czh-glare{opacity:calc(var(--a,0) * .45)}

/* Purpurina arcoíris */
.czh[data-fx="ultra"] .czh-shine,.czh[data-fx="sir"] .czh-shine{display:block;
  background-image:${rainbow};background-size:200% 200%;background-position:var(--P);
  -webkit-mask-image:url(${glitter});mask-image:url(${glitter});
  -webkit-mask-size:256px 256px;mask-size:256px 256px;
  mix-blend-mode:color-dodge;filter:saturate(1.5) brightness(.95);
  opacity:calc(.15 + var(--a,0) * .85)}
.czh[data-fx="sir"] .czh-shine{opacity:calc(.08 + var(--a,0) * .55)}

/* Brillo arcoíris amplio alrededor del punto de luz */
.czh[data-fx="ultra"] .czh-sheen,.czh[data-fx="sir"] .czh-sheen{display:block;
  background-image:${rainbow};background-size:300% 300%;background-position:var(--Pi);
  mix-blend-mode:soft-light;opacity:calc(var(--a,0) * .55);
  -webkit-mask-image:radial-gradient(farthest-corner circle at var(--P),#000 0%,rgba(0,0,0,.3) 55%,transparent 100%);
  mask-image:radial-gradient(farthest-corner circle at var(--P),#000 0%,rgba(0,0,0,.3) 55%,transparent 100%)}

/* Textura que sigue el trazo del dibujo */
.czh[data-fx="sir"] .czh-etch,.czh[data-fx="gold"] .czh-etch{display:block;
  background-image:${rainbow};background-size:180% 180%;background-position:var(--Pi);
  -webkit-mask-image:var(--edge,url(${glitter}));mask-image:var(--edge,url(${glitter}));
  -webkit-mask-size:var(--edge-size,256px 256px);mask-size:var(--edge-size,256px 256px);
  mix-blend-mode:color-dodge;filter:saturate(1.2) brightness(.75);
  opacity:calc(.08 + var(--a,0) * .7)}
.czh[data-fx="gold"] .czh-etch{background-image:${gold};filter:brightness(.9)}

/* Destellos cerca de la luz */
.czh[data-fx="ultra"] .czh-glint,.czh[data-fx="sir"] .czh-glint,.czh[data-fx="gold"] .czh-glint{display:block;
  background:radial-gradient(circle at var(--P),#fff 0%,rgba(255,255,255,.65) 16%,transparent 40%);
  -webkit-mask-image:url(${glint});mask-image:url(${glint});
  -webkit-mask-size:256px 256px;mask-size:256px 256px;
  mix-blend-mode:screen;opacity:calc(var(--a,0) * .95)}

/* Oro */
.czh[data-fx="gold"] .czh-shine{display:block;background-image:${gold};background-size:220% 220%;
  background-position:var(--P);
  -webkit-mask-image:url(${glitter});mask-image:url(${glitter});
  -webkit-mask-size:256px 256px;mask-size:256px 256px;
  mix-blend-mode:color-dodge;opacity:calc(.15 + var(--a,0) * .85)}
.czh[data-fx="gold"] .czh-sheen{display:block;background-image:${gold};background-size:300% 300%;
  background-position:var(--Pi);mix-blend-mode:overlay;opacity:calc(var(--a,0) * .5)}

/* Holo clásico: solo la ventana de la ilustración */
.czh[data-fx="holo"] .czh-shine{display:block;inset:auto;${ART};
  background-image:repeating-linear-gradient(0deg,rgba(0,0,0,.28) 0 1px,transparent 1px 3px),${rainbowV};
  background-size:100% 100%,300% 100%;background-position:0 0,calc(var(--px,.5) * 100%) 0;
  mix-blend-mode:color-dodge;filter:brightness(.55) contrast(1.5) saturate(1.2);
  opacity:calc(.2 + var(--a,0) * .65)}

/* Reverse holo: todo menos la ilustración */
.czh[data-fx="reverse"] .czh-shine{display:block;background-image:${rainbow};background-size:220% 220%;
  background-position:var(--P);
  -webkit-mask:linear-gradient(#000 0 0),linear-gradient(#000 0 0) ${ART_MASK} no-repeat;-webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0),linear-gradient(#000 0 0) ${ART_MASK} no-repeat;mask-composite:exclude;
  mix-blend-mode:color-dodge;filter:brightness(.42) contrast(1.6) saturate(1.1);
  opacity:calc(.15 + var(--a,0) * .55)}

/* La foto original se queda debajo (recibe los clics del tema) sin verse:
   en la ficha solo se aparta el dibujo para conservar su fondo redondeado */
.czh-src-g{object-position:-400vw 0!important;transition-property:none!important}
.czh-src-lb{opacity:0!important}
.pswp--zoomed-in .czh{display:none}
.pswp--zoomed-in .czh-src-lb{opacity:1!important}
`;
    const style = document.createElement('style');
    style.id = 'czholo-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ---------- giroscopio (solo donde no hace falta pedir permiso) ---------- */

  const gyro = { on: false, x: 0, y: 0, base: null, t: 0 };
  function startGyro() {
    if (gyro.on || typeof DeviceOrientationEvent === 'undefined') return;
    if (typeof DeviceOrientationEvent.requestPermission === 'function') return; // iPhone: sin avisos de permiso
    if (!matchMedia('(pointer: coarse)').matches) return;
    gyro.on = true;
    window.addEventListener('deviceorientation', (e) => {
      if (e.beta == null || e.gamma == null) return;
      const X = e.gamma, Y = e.beta;
      if (!gyro.base) gyro.base = { X, Y };
      // la posición neutra se recalibra poco a poco según cómo se sujete el móvil
      gyro.base.X += (X - gyro.base.X) * 0.01;
      gyro.base.Y += (Y - gyro.base.Y) * 0.01;
      gyro.x = clamp((X - gyro.base.X) / 20, -1, 1);
      gyro.y = clamp((Y - gyro.base.Y) / 20, -1, 1);
      gyro.t = performance.now();
    });
  }

  /* ---------- cartas activas ---------- */

  const active = new Set();
  let raf = 0;
  const pointer = { x: -1, y: -1, t: 0, type: 'mouse' };

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const en of entries) {
          const e = en.target.__czhEntry;
          if (e) e.visible = en.isIntersecting;
        }
        kick();
      }, { rootMargin: '80px' })
    : null;

  function effectFor(img) {
    if (EFFECTS.includes(forced)) return forced;
    try {
      const m = new URL(img.currentSrc || img.src, location.href).pathname.match(FILE_RE);
      return m ? m[1].toLowerCase() : null;
    } catch {
      return null;
    }
  }

  function place(e) {
    const img = e.img;
    if (e.mode === 'lb') {
      const s = img.style;
      e.box.style.width = s.width || img.offsetWidth + 'px';
      e.box.style.height = s.height || img.offsetHeight + 'px';
    } else {
      e.box.style.left = img.offsetLeft + 'px';
      e.box.style.top = img.offsetTop + 'px';
      e.box.style.width = img.offsetWidth + 'px';
      e.box.style.height = img.offsetHeight + 'px';
    }
  }

  function loadEdges(e) {
    if (!EDGE_EFFECTS.has(e.fx)) return;
    edgeMap(e.src).then((url) => {
      if (!e.box.isConnected || !url) return;
      e.face.style.setProperty('--edge', `url(${url})`);
      e.face.style.setProperty('--edge-size', '100% 100%');
    });
  }

  function teardown(e) {
    e.mo.disconnect();
    if (e.ro) e.ro.disconnect();
    if (io) io.unobserve(e.box);
    e.box.remove();
    e.img.classList.remove('czh-src-g', 'czh-src-lb');
    e.img.__czh = null;
    active.delete(e);
  }

  function enhance(img, mode) {
    if (img.__czh || img.classList.contains('czh-base') || img.closest('.czh')) return;
    if (img.classList.contains('pswp__img--placeholder')) return;
    const fx = effectFor(img);
    if (!fx || (fx === 'none' && forced !== 'none')) return;
    const host = img.parentElement;
    const src = img.currentSrc || img.src;
    if (!host || !src) return;

    const box = document.createElement('div');
    box.className = 'czh';
    box.dataset.fx = fx;
    box.setAttribute('aria-hidden', 'true');
    box.innerHTML =
      '<div class="czh-face"><img class="czh-base" alt="" draggable="false">' +
      '<div class="czh-l czh-sheen"></div><div class="czh-l czh-shine"></div><div class="czh-l czh-etch"></div>' +
      '<div class="czh-l czh-glint"></div><div class="czh-l czh-glare"></div></div>';
    const face = box.firstElementChild;
    const base = face.firstElementChild;
    face.style.setProperty('--czh-src', 'url("' + src.replace(/"/g, '%22') + '")');
    base.src = src;

    const e = {
      img, box, face, fx, mode, src, visible: true,
      c: { x: 0.5, y: 0.5, a: REST }, v: { x: 0, y: 0, a: 0 }, t: { x: 0.5, y: 0.5, a: REST },
      phase: Math.random() * Math.PI * 2,
    };
    img.__czh = e;
    box.__czhEntry = e;
    if (mode === 'g') host.classList.add('czh-host');
    host.appendChild(box);
    place(e);

    // la original se aparta solo cuando la copia ya se ve (sin parpadeos)
    const reveal = () => img.classList.add(mode === 'g' ? 'czh-src-g' : 'czh-src-lb');
    if (base.complete && base.naturalWidth) reveal();
    else base.addEventListener('load', reveal, { once: true });

    e.mo = new MutationObserver(() => {
      const now = img.currentSrc || img.src;
      if (now && now !== e.src && !now.startsWith('data:')) {
        // otra foto (cambio de variante o carga en mejor resolución)
        teardown(e);
        tryEnhance(img, mode);
        return;
      }
      place(e);
    });
    e.mo.observe(img, { attributes: true, attributeFilter: ['style', 'width', 'height', 'src', 'srcset'] });
    if (mode === 'g' && 'ResizeObserver' in window) {
      e.ro = new ResizeObserver(() => place(e));
      e.ro.observe(img);
    }
    if (io) io.observe(box);

    loadEdges(e);
    active.add(e);
    startGyro();
    kick();
  }

  function isLive(e) {
    if (!e.visible) return false;
    if (e.mode === 'lb') {
      const item = e.img.closest('.pswp__item');
      if (item && item.getAttribute('aria-hidden') === 'true') return false;
      const root = e.img.closest('.pswp');
      return !root || !root.classList.contains('pswp--zoomed-in');
    }
    // con el visor abierto, la de la ficha descansa
    return !document.querySelector('.pswp--open');
  }

  function kick() {
    if (!raf && active.size) raf = requestAnimationFrame(tick);
  }

  function tick(now) {
    raf = 0;
    for (const e of active) if (!e.img.isConnected) teardown(e);
    const still = reduceMotion.matches;
    const gyroLive = gyro.on && now - gyro.t < 1000;
    let any = false;
    for (const e of active) {
      if (!isLive(e)) continue;
      any = true;
      const r = e.box.getBoundingClientRect();
      const recent = now - pointer.t < (pointer.type === 'mouse' ? 1e9 : 2500);
      const inside = recent && r.width &&
        pointer.x >= r.left && pointer.x <= r.right && pointer.y >= r.top && pointer.y <= r.bottom;
      const t = e.t, c = e.c, v = e.v;
      if (inside) {
        t.x = (pointer.x - r.left) / r.width;
        t.y = (pointer.y - r.top) / r.height;
        t.a = 1;
      } else if (gyroLive) {
        t.x = 0.5 + gyro.x * 0.5;
        t.y = 0.5 + gyro.y * 0.5;
        t.a = 0.4 + Math.min(1, Math.hypot(gyro.x, gyro.y) * 1.5) * 0.6;
      } else if (!still) {
        // se mueve sola, como en la vitrina
        const s = now / 1000 + e.phase;
        t.x = 0.5 + 0.28 * Math.sin(s * 0.55);
        t.y = 0.5 + 0.2 * Math.sin(s * 0.83 + 1.3);
        t.a = 0.62;
      } else {
        t.x = 0.5; t.y = 0.5; t.a = REST;
      }
      // muelle amortiguado: movimiento suave, sin saltos
      const k = inside ? 0.14 : 0.06, damp = 0.76;
      for (const key of ['x', 'y', 'a']) {
        v[key] = (v[key] + (t[key] - c[key]) * k) * damp;
        c[key] += v[key];
      }
      const dx = c.x - 0.5, dy = c.y - 0.5;
      const tilt = still ? 0 : TILT;
      const st = e.box.style;
      st.setProperty('--px', c.x.toFixed(4));
      st.setProperty('--py', c.y.toFixed(4));
      st.setProperty('--a', clamp(c.a, 0, 1).toFixed(3));
      st.setProperty('--rx', (-dy * 2 * tilt).toFixed(2) + 'deg');
      st.setProperty('--ry', (dx * 2 * tilt).toFixed(2) + 'deg');
      st.setProperty('--s', (1 + Math.max(0, c.a - 0.7) * 0.1).toFixed(4));
      st.setProperty('--sx', (-dx * 26).toFixed(1));
      st.setProperty('--sy', (12 - dy * 26).toFixed(1));
    }
    if (any) kick();
  }

  /* ---------- enganche con el tema ---------- */

  const onPointer = (ev) => {
    pointer.x = ev.clientX;
    pointer.y = ev.clientY;
    pointer.t = performance.now();
    pointer.type = ev.pointerType || 'mouse';
    kick();
  };
  document.addEventListener('pointermove', onPointer, { passive: true, capture: true });
  document.addEventListener('pointerdown', onPointer, { passive: true, capture: true });
  document.documentElement.addEventListener('pointerleave', () => { pointer.t = 0; }, { passive: true });

  function tryEnhance(img, mode) {
    if (img.__czh) return;
    if (img.complete && img.naturalWidth) enhance(img, mode);
    else img.addEventListener('load', () => enhance(img, mode), { once: true });
  }

  function scan(node) {
    if (!(node instanceof Element)) return;
    if (node.matches(LIGHTBOX_IMG)) tryEnhance(node, 'lb');
    else if (node.matches(GALLERY_IMG)) tryEnhance(node, 'g');
    node.querySelectorAll(LIGHTBOX_IMG).forEach((n) => tryEnhance(n, 'lb'));
    node.querySelectorAll(GALLERY_IMG).forEach((n) => tryEnhance(n, 'g'));
  }

  function start() {
    injectCSS();
    new MutationObserver((muts) => {
      for (const m of muts) m.addedNodes.forEach(scan);
      kick();
    }).observe(document.body, { childList: true, subtree: true });
    scan(document.body);
    // al cerrar el visor, la de la ficha vuelve a moverse
    setInterval(() => { if (active.size) kick(); }, 1000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();